import { BookOutlined, DownOutlined, FileTextOutlined, RightOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import sparkles from '@assets/CustomIcons/sparkles.svg'
import { DeliveryRowDetails } from '@components/InventoryValuation/Costing/Details/DeliveryRowDetails'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import moment from 'moment/moment'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { SimpleRow } from './SimpleRow'

export function DeliveryRow({
  row,
  index,
  rowIndex,
  openOrderModal,
  reasonCodes,
  inventory_costing_identifier,
  setTotals,
  totals,
  setExpandedRows,
  expandedRows,
  scopes,
  date,
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasMovementError = row?.has_errors || row?.missing_prices
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const orderMvmtButtonTitle = !hasMovementError
    ? 'View Order Movements'
    : row?.is_projected
      ? 'Missing or Expired Price'
      : row?.missing_prices
        ? 'BOL is missing prices'
        : 'Movement Has Validation Errors'

  useEffect(() => {
    if (expandedRows?.includes(rowIndex)) {
      setIsExpanded(true)
    }
  }, [expandedRows])

  const handleExpand = () => {
    const exp = isExpanded
    setIsExpanded(!isExpanded)
    setExpandedRows((prev) => {
      if ((!prev || !prev?.includes(rowIndex)) && !exp) {
        return [...prev, rowIndex]
      }
      if (prev?.includes(rowIndex) && exp) {
        return prev.filter((i) => i !== rowIndex)
      }
    })
  }
  const orderReadScope = scopes.scopes.includes('o.r')
  const orderMvmtScope = scopes.scopes.includes('om.p')

  if (row?.is_temporary) return <SimpleRow layer='builds' rowIndex={rowIndex} row={row} isTemp />

  return (
    <>
      <Horizontal
        key={`middle_row${index}-${rowIndex}`}
        className='bg-1 border-bottom'
        style={{ width: '100%', borderLeft: '2px solid var(--theme-color-2)' }}
        alignItems='center'
      >
        <Vertical className='p-2' flex={1.5} justifyContent='center'>
          {row.order_id ? (
            <Horizontal>
              {orderReadScope && (
                <GraviButton
                  style={{ borderRadius: '50%', height: 'fit-content', width: 'fit-content' }}
                  icon={<FileTextOutlined />}
                  onClick={() => openOrderModal(row.order_id)}
                  title='View Order'
                  className='mr-2 p-2'
                />
              )}
              {orderMvmtScope && (
                <Link
                  target='_blank'
                  to={`/order_movements_page/report?${new URLSearchParams({ order_number: row.order_number })}`}
                >
                  <GraviButton
                    error={hasMovementError}
                    icon={<BookOutlined />}
                    title={orderMvmtButtonTitle}
                    style={{ borderRadius: '50%', height: 'fit-content', width: 'fit-content' }}
                    className='mr-2 p-2'
                  />
                </Link>
              )}
              <GraviButton
                style={{
                  background: 'transparent',
                  border: 'none',
                  height: 'fit-content',
                  width: 'fit-content',
                }}
                className='p-2'
                title={isExpanded ? 'Collapse' : 'Expand'}
                icon={
                  isExpanded ? <DownOutlined className='text-gray-500' /> : <RightOutlined className='text-gray-500' />
                }
                onClick={handleExpand}
              />
            </Horizontal>
          ) : (
            <GraviButton
              style={{
                background: 'transparent',
                border: 'none',
                height: 'fit-content',
                width: 'fit-content',
              }}
              className='p-2'
              title={isExpanded ? 'Collapse' : 'Expand'}
              icon={
                isExpanded ? <DownOutlined className='text-gray-500' /> : <RightOutlined className='text-gray-500' />
              }
              onClick={handleExpand}
            />
          )}
        </Vertical>
        <Horizontal className='p-2' flex={1.2} justifyContent='center' alignItems='center'>
          {row.is_projected && <img src={sparkles} className='mr-2' alt='sparkles' style={{ height: '12px' }} />}
          <Texto appearance={row?.is_projected && 'secondary'}>
            {row.date && moment(row.date).format('MM/DD/YYYY')}
          </Texto>
        </Horizontal>
        <Vertical className='p-2' flex={1} justifyContent='center' alignItems='center'>
          <Texto className='text-truncate' align='center' style={{ width: '100%' }}>
            {(row.bol_numbers && row.bol_numbers[0]) || 'N/A'}
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent='center' alignItems='center'>
          <Texto className='text-truncate' align='center' style={{ width: '100%' }}>
            {row.order_number || 'N/A'}
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1.5} justifyContent='center'>
          <Texto className='text-truncate' align='center' style={{ width: '100%' }}>
            {(row.suppliers && row.suppliers[0]) || 'N/A'}
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent='center' alignItems='flex-end'>
          <Texto className='text-truncate' align='right' style={{ width: '100%' }}>
            {addCommasToNumber(row.volume, 0) ?? 'N/A'}
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent='center' alignItems='flex-end'>
          <Texto className='text-truncate' align='right' style={{ width: '100%' }}>
            {formattedCurrency}
            {addCommasToNumber(row?.price, 4) ?? 'N/A'}
          </Texto>
        </Vertical>
        <Vertical className='p-2 mr-2' flex={1} justifyContent='center' alignItems='flex-end'>
          <Texto className='text-truncate text-secondary' align='right' style={{ width: '100%' }}>
            {formattedCurrency}
            {addCommasToNumber(row?.value, 2) ?? 'N/A'}
          </Texto>
        </Vertical>
      </Horizontal>
      {isExpanded && (
        <DeliveryRowDetails
          reasonCodes={reasonCodes}
          row={row}
          inventory_costing_identifier={inventory_costing_identifier}
          totals={totals}
          setTotals={setTotals}
          setExpandedRows={setExpandedRows}
          expandedRows={expandedRows}
          setIsExpanded={setIsExpanded}
          canEdit={scopes?.valuation?.write}
          date={date}
        />
      )}
    </>
  )
}
